import type { EntityReference } from "@firecms/core";
import { getApp } from "firebase/app";
import { doc, getFirestore, onSnapshot } from "firebase/firestore";
import { useEffect, useState, type ReactNode } from "react";
import { parseEventTask, type EventTask } from "./eventTask";
import type { EventStatus } from "../properties/eventStatus";

type ChipVariant = "version" | "empty" | "publishing" | "failed";

const CHIP_STYLES: Record<ChipVariant, string> = {
  version:
    "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200 ring-1 ring-inset ring-sky-200/80 dark:ring-sky-800",
  empty:
    "bg-amber-50 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200 ring-1 ring-inset ring-amber-200/80 dark:ring-amber-900",
  publishing:
    "bg-violet-50 text-violet-800 dark:bg-violet-950/60 dark:text-violet-200 ring-1 ring-inset ring-violet-200/80 dark:ring-violet-900",
  failed:
    "bg-rose-50 text-rose-800 dark:bg-rose-950/60 dark:text-rose-200 ring-1 ring-inset ring-rose-200/80 dark:ring-rose-900",
};

function ContentReleaseChip({
  variant,
  children,
}: {
  variant: ChipVariant;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${CHIP_STYLES[variant]}`}
    >
      {children}
    </span>
  );
}

const versionFromRef = (ref?: EntityReference): number | undefined => {
  if (!ref?.id) {
    return undefined;
  }
  const parsed = Number.parseInt(ref.id, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

export function EventContentReleaseChips({
  contentReleaseRef,
  status,
  eventPath,
}: {
  contentReleaseRef?: EntityReference;
  status?: EventStatus;
  /** `accounts/{accountId}/events/{eventId}` */
  eventPath: string;
}) {
  const [task, setTask] = useState<EventTask | null>(null);
  const version = versionFromRef(contentReleaseRef);

  useEffect(() => {
    if (!eventPath) {
      setTask(null);
      return;
    }
    const firestore = getFirestore(getApp());
    return onSnapshot(doc(firestore, `${eventPath}/tasks/contentPublish`), (snapshot) => {
      setTask(parseEventTask(snapshot.data()));
    });
  }, [eventPath]);

  if (task?.status === "running") {
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        {version != null ? (
          <ContentReleaseChip variant="version">v{version}</ContentReleaseChip>
        ) : null}
        <ContentReleaseChip variant="publishing">
          Publicando… {task.progress}%
        </ContentReleaseChip>
      </span>
    );
  }

  if (task?.status === "failed") {
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        {version != null ? (
          <ContentReleaseChip variant="version">v{version}</ContentReleaseChip>
        ) : null}
        <ContentReleaseChip variant="failed">Error al publicar</ContentReleaseChip>
      </span>
    );
  }

  if (version == null) {
    if (status === "provisioning") {
      return (
        <ContentReleaseChip variant="publishing">Publicando…</ContentReleaseChip>
      );
    }

    if (status === "provisioning_failed") {
      return (
        <ContentReleaseChip variant="failed">Error al configurar</ContentReleaseChip>
      );
    }

    return (
      <ContentReleaseChip variant="empty">Sin publicar</ContentReleaseChip>
    );
  }

  return (
    <ContentReleaseChip variant="version">v{version}</ContentReleaseChip>
  );
}
